import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  cn,
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { RefreshCw, ExternalLink, Loader2, Copy, Check, Columns2, Maximize2, Smartphone } from "lucide-react";
import { useEnduserIsAuthenticated, useEnduserCurrentEnduser, useEnduserLogout } from "../../enduser/store/hooks";
import { useAuth } from "@tellescope/tanstack-query";
import { useOrganization } from "../../shared/store/organizationStore";
import { PortalContent } from "../../enduser/components/PortalContent";
import { EnduserQueryProvider } from "../../enduser/components/EnduserQueryProvider";
import { invalidateEnduserQueries } from "../../enduser/components/enduserQueryClient";
import { VideoCallingProvider } from "../../enduser/providers/VideoCallingProvider";
import { useThemeStyles } from "../../shared/hooks/useThemeStyles";
import { useFontLoader } from "../../shared/hooks/useFontLoader";
import { PortalSchemaProvider } from "../../shared/context/PortalSchemaContext";
import type { PortalSchema } from "../../shared/schema";
import type { PreviewViewportMode } from "../pages/DashboardPage";

interface PortalPreviewProps {
  schema: PortalSchema;
  onSelectEnduser: () => void;
  /**
   * Optional: When provided, the preview will sync to this page when it changes.
   * Used by admin view to sync page selection with the preview.
   * When not provided (patient-facing view), the preview manages its own navigation.
   */
  selectedPageId?: string | null;
  /** Current viewport mode for the preview */
  viewportMode?: PreviewViewportMode;
  /** Callback to change the viewport mode */
  onViewportModeChange?: (mode: PreviewViewportMode) => void;
}

/** Helper component for displaying a copyable field */
const CopyableField: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <div className="min-w-0 flex-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <p className="text-sm font-mono truncate">{value}</p>
      </div>
      <button
        onClick={handleCopy}
        className="flex-shrink-0 p-1 rounded hover:bg-muted transition-colors"
        title={copied ? "Copied!" : "Copy to clipboard"}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-500" />
        ) : (
          <Copy className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </button>
    </div>
  );
};

export const PortalPreview: React.FC<PortalPreviewProps> = ({
  schema,
  onSelectEnduser,
  selectedPageId,
  viewportMode,
  onViewportModeChange,
}) => {
  const { t } = useTranslation();
  const isAuthenticated = useEnduserIsAuthenticated();
  const currentEnduser = useEnduserCurrentEnduser();
  const logout = useEnduserLogout();
  const { session } = useAuth();
  const organization = useOrganization();
  const [currentPageId, setCurrentPageId] = useState<string | null>(schema.pages[0]?.id ?? null);

  // Generate scoped theme styles for the preview (doesn't affect admin UI)
  const themeStyles = useThemeStyles(schema.settings?.theming);

  // Load custom font for the preview
  useFontLoader(schema.settings?.theming?.fontFamily, schema.settings?.theming?.customFontUrl);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isOpeningLivePortal, setIsOpeningLivePortal] = useState(false);

  const handleOpenLivePortal = useCallback(async () => {
    if (!session || !currentEnduser) return;

    setIsOpeningLivePortal(true);

    try {
      // Generate a fresh auth token for the current enduser
      const tokenResult = await session.api.endusers.generate_auth_token({
        id: currentEnduser.id,
        overrideConsent: true,
        overrideOTP: true,
      });

      // Determine the page path - use "/" for first page, otherwise use slug
      const firstPage = schema.pages[0];
      const activePage = schema.pages.find((p) => p.id === currentPageId) ?? firstPage;
      const pagePath = activePage && activePage.slug !== firstPage?.slug ? `/${activePage.slug}` : "/";

      // Build the live portal URL with the current page path
      const livePortalURL = `${window.location.origin}${pagePath}?token=${encodeURIComponent(tokenResult.authToken)}`;

      // Log URL for debugging - allows easy copy/paste to test token behavior
      // console.log("[View Live] Opening portal URL:", livePortalURL);

      // Open in new tab
      window.open(livePortalURL, "_blank");
    } catch (err) {
      console.error("Failed to open live portal:", err);
    } finally {
      setIsOpeningLivePortal(false);
    }
  }, [session, currentEnduser, schema.pages, currentPageId]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // Run invalidation and minimum delay in parallel, wait for both
      await Promise.all([
        invalidateEnduserQueries(),
        new Promise((resolve) => setTimeout(resolve, 500)), // Minimum 500ms animation
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // Sync with external selectedPageId when provided (admin view)
  // This allows the admin to control which page is shown while still allowing
  // the preview to navigate independently via the navbar
  useEffect(() => {
    if (selectedPageId !== undefined && selectedPageId !== currentPageId) {
      setCurrentPageId(selectedPageId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally excluding currentPageId to allow one-way sync from admin
  }, [selectedPageId]);

  // Find current page from schema
  const currentPage = schema.pages.find((p) => p.id === currentPageId) ?? schema.pages[0] ?? null;

  // If no enduser session, show selector prompt
  if (!isAuthenticated) {
    return (
      <div className="h-full flex flex-col">
        {/* Header matching the authenticated state */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-muted border-b">
          <div className="text-sm">
            <span className="text-muted-foreground">{t("Portal Preview")}</span>
          </div>
          <Button variant="outline" size="sm" onClick={onSelectEnduser}>
            {t("Select Enduser")}
          </Button>
        </div>

        {/* Prompt content */}
        <div className="flex-1 flex items-center justify-center bg-muted/30 p-8">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle className="text-xl">{t("No Enduser Selected")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-center">
              <p className="text-muted-foreground">{t("Select an enduser to preview the portal as them.")}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Render the actual enduser portal
  return (
    <EnduserQueryProvider>
      <VideoCallingProvider>
        <div className="h-full flex flex-col">
          {/* Preview header with enduser info */}
          <div className="flex items-center justify-between px-4 py-2.5 bg-muted border-b">
            <div className="text-sm">
              <span className="text-muted-foreground">{t("Viewing as")}:</span>{" "}
              <HoverCard openDelay={200} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <span className="font-medium cursor-help">
                    {currentEnduser?.fname || currentEnduser?.lname
                      ? `${currentEnduser?.fname ?? ""} ${currentEnduser?.lname ?? ""}`.trim()
                      : currentEnduser?.email ?? t("Unknown")}
                  </span>
                </HoverCardTrigger>
                <HoverCardContent className="w-72" align="start">
                  <div className="space-y-1">
                    <h4 className="text-sm font-semibold mb-2">{t("Enduser Details")}</h4>
                    {currentEnduser?.id && <CopyableField label={t("ID")} value={currentEnduser.id} />}
                    {currentEnduser?.email && <CopyableField label={t("Email")} value={currentEnduser.email} />}
                    {currentEnduser?.phone && <CopyableField label={t("Phone")} value={currentEnduser.phone} />}
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
            <div className="flex items-center gap-2">
              {/* Viewport mode toggle - ordered from widest to narrowest */}
              {onViewportModeChange && (
                <div className="flex items-center border rounded-md bg-muted/50">
                  <Button
                    variant={viewportMode === "fullscreen" ? "default" : "ghost"}
                    size="sm"
                    className={cn("h-7 px-2 rounded-r-none", viewportMode !== "fullscreen" && "hover:bg-muted")}
                    onClick={() => onViewportModeChange("fullscreen")}
                    title={t("Fullscreen preview")}
                  >
                    <Maximize2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewportMode === "split" ? "default" : "ghost"}
                    size="sm"
                    className={cn("h-7 px-2 rounded-none border-x", viewportMode !== "split" && "hover:bg-muted")}
                    onClick={() => onViewportModeChange("split")}
                    title={t("50/50 split view")}
                  >
                    <Columns2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewportMode === "mobile" ? "default" : "ghost"}
                    size="sm"
                    className={cn("h-7 px-2 rounded-l-none", viewportMode !== "mobile" && "hover:bg-muted")}
                    onClick={() => onViewportModeChange("mobile")}
                    title={t("Mobile preview")}
                  >
                    <Smartphone className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleOpenLivePortal}
                disabled={isOpeningLivePortal}
                title={t("Open live portal in new tab")}
              >
                {isOpeningLivePortal ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4" />
                )}
                <span className="ml-1">{t("View Live")}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                title={t("Refresh data")}
              >
                <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
              </Button>
              <Button variant="outline" size="sm" onClick={onSelectEnduser}>
                {t("Change")}
              </Button>
            </div>
          </div>

          {/* Themed portal content wrapper - scopes CSS variables to preview only */}
          <div className="flex-1 flex flex-col overflow-hidden" style={themeStyles}>
            <PortalSchemaProvider schema={schema}>
              <PortalContent
                schema={schema}
                currentPage={currentPage}
                onNavigate={setCurrentPageId}
                onLogout={logout}
                logoURL={organization?.logoURL}
                organizationName={organization?.name}
                showSidebar={schema.settings?.layout?.enableSidebarNavigation ?? false}
                emptyMessage={t("No blocks yet. Add blocks from the editor to see them here.")}
              />
            </PortalSchemaProvider>
          </div>
        </div>
      </VideoCallingProvider>
    </EnduserQueryProvider>
  );
};
